import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useLazyGetSftpPartnerWaitingFilesQuery } from "store/queries/sftpPartners";
import { ISftpWaitingFile } from "types/SftpPartner";

import { formatDateTime } from "../../utils/hstImports";
import { formatBytes, sftpApiErrorMessage } from "../../utils/sftpPartners";
import { SftpDownloadButton, useSftpFileDownload } from "./sftpFileParts";

/**
 * CLE-2958 — the keys in the partner's S3 prefix that no ingest has recorded yet. Listing the prefix costs
 * an S3 call per 1,000 keys, so it runs only when someone presses the button, never on page load.
 */
export function WaitingInBucketCard({ slug }: { slug: string }) {
  const [list, { data, isFetching, isError, error }] =
    useLazyGetSftpPartnerWaitingFilesQuery();
  const { download, pendingKey } = useSftpFileDownload(slug);
  const result = data?.data;
  const hidden = result ? result.total - result.files.length : 0;

  const nameBody = (f: ISftpWaitingFile) => (
    <span className="flex align-items-center gap-2">
      <code style={{ wordBreak: "break-all" }}>{f.name}</code>
      {f.inScope ? null : (
        <Tag
          value="Not swept"
          severity="info"
          title="The daily job does not read this file (outside its folders or include tokens)"
        />
      )}
    </span>
  );

  const actionBody = (f: ISftpWaitingFile) => (
    <SftpDownloadButton
      s3Key={f.s3Key}
      onDownload={download}
      loading={pendingKey === f.s3Key}
    />
  );

  return (
    <Card title="Waiting in bucket" className="mb-3">
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <span className="text-600 text-sm">
          Files in the partner&apos;s S3 folder that no import has recorded yet.
          Files we write for the partner are not listed.
        </span>
        <Button
          label={result ? "Refresh" : "List bucket"}
          icon="pi pi-refresh"
          className="p-button-outlined p-button-sm"
          loading={isFetching}
          onClick={() => list(slug)}
          data-testid="sftp-waiting-refresh"
        />
      </div>
      {isError ? (
        <Message
          severity="error"
          text={sftpApiErrorMessage(error, "Couldn't list the bucket.")}
        />
      ) : null}
      {result && !isError ? (
        <>
          <div className="mb-2" data-testid="sftp-waiting-summary">
            {result.total === 0
              ? "Nothing waiting."
              : `${result.total} file${
                  result.total === 1 ? "" : "s"
                } waiting, ${
                  result.inScopeCount
                } of them read by the daily job.`}{" "}
            <span className="text-600 text-sm">
              Listed {formatDateTime(result.listedAt)}. Last sweep:{" "}
              {result.lastSweepAt
                ? `${formatDateTime(result.lastSweepAt)} (${
                    result.filesWaitingInBucket ?? 0
                  } waiting then)`
                : "never"}
              .
            </span>
          </div>
          {result.files.length ? (
            <DataTable
              value={result.files}
              dataKey="s3Key"
              responsiveLayout="scroll"
              data-testid="sftp-waiting-files"
            >
              <Column header="Name" body={nameBody} />
              <Column
                header="Size"
                body={(f: ISftpWaitingFile) => formatBytes(f.byteSize)}
              />
              <Column
                header="Last modified"
                body={(f: ISftpWaitingFile) => formatDateTime(f.lastModified)}
              />
              <Column body={actionBody} style={{ width: "4rem" }} />
            </DataTable>
          ) : null}
          {hidden > 0 ? (
            <div className="mt-2 text-600" data-testid="sftp-waiting-more">
              and {hidden} more
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
